


import React, { useState, useRef, useEffect } from 'react';
import { useFavorites } from '../../contexts/FavoritesContext';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'sonner';
import { ADD_TO_CART_TOAST_DURATION } from '../../lib/toastConfig';
import { webpVariant } from '../../lib/optimizedImage';
import ProductPrice from './ProductPrice';
import { getProductDisplayName } from '../../utils/productDisplay';

const isVideoSrc = (src) =>
  typeof src === 'string' && /\.(mp4|mov|webm|m4v)$/i.test(src);

const buildProductMedia = (product) => {
  const sourceMedia = [];

  if (product.videoUrl) {
    sourceMedia.push({
      type: 'video',
      src: product.videoUrl,
      poster: product.image,
    });
  }

  const rawImages = (product.images && product.images.length > 0
    ? product.images
    : [product.image]
  ).filter(Boolean);

  rawImages.forEach((src) => {
    if (sourceMedia.some((media) => media.src === src)) return;
    if (isVideoSrc(src)) {
      sourceMedia.push({ type: 'video', src, poster: product.image });
    } else {
      sourceMedia.push({ type: 'image', src });
    }
  });

  return sourceMedia;
};

const getUniqueMediaSources = (mediaList) =>
  mediaList.filter(
    (media, index, items) =>
      items.findIndex((entry) => entry.src === media.src) === index
  );

const getPrimaryImageMedia = (product) => {
  const rawImages = (product.images && product.images.length > 0
    ? product.images
    : [product.image]
  ).filter(Boolean);

  const firstImage = rawImages.find((src) => !isVideoSrc(src)) || product.image;
  return firstImage
    ? {
        type: 'image',
        src: firstImage,
      }
    : null;
};

const getProductImageGallery = (product) =>
  (product.images && product.images.length > 0
    ? product.images
    : [product.image]
  )
    .filter(Boolean)
    .map((src) => ({
      image: src,
      mediaType: 'image',
      poster: product.image,
    }));

const getLibraryProductKey = (product) =>
  `${product.id}:${product.name}:${product.mainCategories}`;

const getEligibleLibraryProducts = (libraryProducts) =>
  libraryProducts
    .filter(Boolean)
    .filter(
      (item, index, items) =>
        items.findIndex(
          (entry) => getLibraryProductKey(entry) === getLibraryProductKey(item)
        ) === index
    );

const rotateProducts = (productList, offset) => {
  if (productList.length === 0) return [];
  const start = ((offset % productList.length) + productList.length) % productList.length;
  return [...productList.slice(start), ...productList.slice(0, start)];
};

const buildCatalogMosaicMedia = (mediaList, maxCells) => {
  const uniqueMedia = getUniqueMediaSources(mediaList);
  if (uniqueMedia.length === 0) return [];

  return Array.from({ length: maxCells }, (_, index) => {
    if (index < uniqueMedia.length) {
      return uniqueMedia[index];
    }
    return { type: 'empty', empty: true };
  });
};

const ProductCard = ({
  product,
  onImageClick,
  showLibraryThumbnails = false,
  libraryProducts = [],
  libraryOffset = 0,
  thumbnailMediaOverrides = [],
  captionOverrides = [],
  previewStartsFromFirstImage = false,
}) => {
  const { toggleFavorite, isFavorite } = useFavorites();
  const { addToCart } = useCart();
  const videoRef = useRef(null); 

  const hasExplicitThumbnailOverrides = thumbnailMediaOverrides.length > 0;
  const sourceMedia = hasExplicitThumbnailOverrides
    ? thumbnailMediaOverrides.map((item) => {
        if (item && typeof item === 'object' && item.empty) {
          return { type: 'empty', empty: true };
        }
        const src = typeof item === 'string' ? item : item.src;
        const productId = typeof item === 'string' ? undefined : item.productId;
        const relatedProduct = productId == null
          ? undefined
          : libraryProducts.find((entry) => entry.id === productId);
        const groupKey = typeof item === 'string' ? undefined : item.groupKey;
        const poster = typeof item === 'string' ? undefined : item.poster;
        // Optional `gallery` lets a single cell open its own multi-item
        // gallery in the right-rail preview (instead of relying on groupKey
        // matching across other cells).
        const gallery = typeof item === 'string' ? undefined : item.gallery;
        return isVideoSrc(src)
          ? {
              type: 'video',
              src,
              product: relatedProduct,
              groupKey,
              gallery,
              poster: poster || product.image,
            }
          : { type: 'image', src, product: relatedProduct, groupKey, gallery };
      })
    : buildProductMedia(product);

  const MAX_CELLS = 9; // Always render a 3 × 3 grid per card.
  const libraryGalleryItems = showLibraryThumbnails
    ? rotateProducts(getEligibleLibraryProducts(libraryProducts), libraryOffset)
        .slice(0, MAX_CELLS)
        .map((item) => {
          const primaryMedia = getPrimaryImageMedia(item);
          return primaryMedia
            ? {
                ...primaryMedia,
                product: item,
              }
            : null;
        })
        .filter(Boolean)
    : [];
  const isCatalogMosaic =
    !showLibraryThumbnails && !hasExplicitThumbnailOverrides;
  const galleryMedia = showLibraryThumbnails
    ? libraryGalleryItems
    : hasExplicitThumbnailOverrides
      ? sourceMedia
      : buildCatalogMosaicMedia(sourceMedia, MAX_CELLS);
  // Caption per cell — honest descriptors (no fake marketing claims).
  // We swap the wording by product category so a medical supply or scrub set
  // doesn't get described with body-care language like "glow" or "hydration".
  const skincareCaptions = [
    { title: "Hero shot", body: "Signature packaging styled for a premium first impression." },
    { title: "See it in motion", body: "Short video — tap to play the product in action." },
    { title: "On real skin", body: "Shown in use — see how it sits on real, glowing skin." },
    { title: "Texture detail", body: "Rich, whipped texture that melts into skin on contact." },
    { title: "Deep hydration", body: "Shea and cocoa butter seal in moisture for 24h softness." },
    { title: "Locks in moisture", body: "Forms a breathable barrier that protects all day." },
    { title: "Glow boost", body: "Targets uneven tone for a visibly luminous finish." },
    { title: "Smooths texture", body: "Refines rough patches on elbows, knees and arms." },
    { title: "Skin-safe formula", body: "No parabens, no sulfates, no harsh fragrance fillers." },
    { title: "Daily ritual", body: "Designed for morning and night use, year-round." },
    { title: "Made in the USA", body: "Small-batch formulated locally with global-grade ingredients." },
    { title: "Customer favorite", body: "One of our most reordered body care pieces this season." },
  ];

  const scrubSetCaptions = [
    { title: "Hero shot", body: "Studio look at the full scrub set, styled for a clinical environment." },
    { title: "See it in motion", body: "Short video — tap to play and see the fit move." },
    { title: "On a real wearer", body: "Shown on body so you can judge the cut and drape." },
    { title: "Fabric detail", body: "Close-up of the weave, stitching and overall finish." },
    { title: "Functional pockets", body: "Pocket layout planned for tools, badges and notes." },
    { title: "Built for shifts", body: "Reinforced seams meant to hold up across long clinical shifts." },
    // Bottom row = "Complete the clinical kit" cross-sell.
    // Cell 6: the scrubs themselves. Cell 7: nitrile gloves. Cell 8: disposable pads.
    { title: "Clinical scrubs", body: "The scrubs themselves — pro-grade build for long clinical shifts." },
    { title: "Nitrile gloves", body: "Pairs with our powder-free disposable nitrile gloves — sold separately." },
    { title: "Disposable pads", body: "Pairs with our multi-use heavy-duty leak-proof pads — sold separately." },
    { title: "Daily wear", body: "Designed for back-to-back shifts, not occasional use." },
    { title: "Pro-grade build", body: "Built to the durability bar clinical buyers actually expect." },
    { title: "Team favorite", body: "One of our most reordered scrub pieces this season." },
  ];

  const medicalSuppliesCaptions = [
    { title: "Pack shot", body: "JMOLUX-branded retail pack — what arrives on your shelf." },
    { title: "See it in motion", body: "Short video — tap to play the product in action." },
    { title: "On the box", body: "Spec, count and use case printed clearly on the pack." },
    { title: "Material detail", body: "Close-up of the material, weave or build quality." },
    { title: "Bulk count", body: "Sized for clinic, salon and household stock-ups." },
    { title: "Single-use", body: "Designed for single-use to support clean hygiene routines." },
    { title: "Easy access", body: "Pack opens flat for fast grab-and-go between patients." },
    { title: "Clinic-grade", body: "Built to the specs healthcare buyers actually look for." },
    { title: "Storage friendly", body: "Stacks and stores neatly without crushing contents." },
    { title: "Daily-use ready", body: "Made for the volume real clinics and households go through." },
    { title: "Distributed in the USA", body: "Distributed by JMOLUX USA to professional and home buyers." },
    { title: "Reorder favorite", body: "One of our most reordered medical supplies this season." },
  ];

  const captionsByCategory = {
    "scrub set": scrubSetCaptions,
    "medical supplies": medicalSuppliesCaptions,
  };
  const cellCaptions = captionsByCategory[product.mainCategories] || skincareCaptions;
  // Homepage passes per-cell captionOverrides; catalog listings use the same
  // category-aware cell captions for each gallery image in the mosaic.
  const showCaptions =
    showLibraryThumbnails || captionOverrides.length > 0 || isCatalogMosaic;
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  useEffect(() => {
    if (videoRef.current && product.videoUrl) {
      const playVideo = async () => {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.log('Autoplay failed:', error);
          // Fallback: play on hover
          const card = videoRef.current.closest('.group');
          if (card) {
            card.addEventListener('mouseenter', () => videoRef.current.play());
            card.addEventListener('mouseleave', () => {
              videoRef.current.pause();
              videoRef.current.currentTime = 0;
            });
          }
        }
      };
      playVideo();
    }
  }, [product.videoUrl]);  

  const handleThumbnailFavoriteClick = (e, targetProduct) => {
    e.stopPropagation();
    toggleFavorite(targetProduct);
  };

  const handleThumbnailAddToCart = (e, targetProduct) => {
    e.stopPropagation();
    addToCart(targetProduct);
    toast.success(`${getProductDisplayName(targetProduct)} added to cart`, {
      duration: ADD_TO_CART_TOAST_DURATION,
    });
  };

  return (
    <div className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
      {/* Top badge row — replaces the badges that used to overlay the big hero image */}
      {(product.freeGift || product.isNew || product.isBestSeller || product.mainCategories !== "medical supplies") && (
        <div className="flex items-center justify-between gap-2 px-3 sm:px-4 pt-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {product.freeGift && (
              <span className="bg-gradient-to-r from-yellow-300 to-amber-400 text-gray-950 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm ring-2 ring-yellow-200">
                Free Gift
              </span>
            )}
            {product.isNew && (
              <span className="bg-amber-400 text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm">
                New
              </span>
            )}
            {product.isBestSeller && (
              <span className="bg-gray-900 text-amber-300 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm">
                Best Seller
              </span>
            )}
          </div>

          {/* Center flashing SALES chip — attention-grabbing call-out that sits
              between the left-side product status chips and the right-side
              Luxury badge. Hidden on medical-supply cards. */}
          {product.mainCategories !== "medical supplies" && !product.freeGift && (
            <span className="animate-flash inline-flex items-center gap-0.5 bg-gradient-to-r from-red-600 via-rose-500 to-red-600 text-white text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-lg ring-2 ring-red-300/70">
              <svg
                className="w-2.5 h-2.5 -ml-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 2.5l1.7 5.2H19l-4.3 3.1 1.7 5.2L12 12.9 7.6 16l1.7-5.2L5 7.7h5.3z" />
              </svg>
              Sales
            </span>
          )}

          {product.mainCategories !== "medical supplies" && (
            <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-3 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full shadow-sm">
              Luxury
            </span>
          )}
        </div>
      )}

      {galleryMedia.length > 0 && (
        <div className="px-3 sm:px-4 pt-3 pb-2">
          <div className="grid grid-cols-3 gap-x-2 gap-y-3 bg-white">
            {galleryMedia.map((media, index) => {
              if (media?.empty) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="flex flex-col items-stretch"
                    aria-hidden="true"
                  >
                    <div className="aspect-square w-full rounded-md border-2 border-transparent bg-transparent" />
                    <div className="mt-1.5 h-3 rounded bg-transparent" />
                    <div className="mt-0.5 h-3 rounded bg-transparent" />
                    <div className="mt-0.5 h-8 rounded bg-transparent" />
                  </div>
                );
              }
              const galleryProduct = media.product || product;
              const priceProduct = media.product || (isCatalogMosaic ? product : null);
              const defaultCaption = showLibraryThumbnails
                ? {
                    title: getProductDisplayName(galleryProduct),
                    body:
                      galleryProduct.description ||
                      galleryProduct.category ||
                      galleryProduct.mainCategories ||
                      '',
                  }
                : showCaptions
                  ? cellCaptions[index % cellCaptions.length]
                  : { title: getProductDisplayName(galleryProduct), body: '' };
              const caption = showCaptions
                ? {
                    ...defaultCaption,
                    ...(captionOverrides[index] || {}),
                  }
                : { title: getProductDisplayName(galleryProduct), body: '' };
              const isActive = activeImageIndex === index;
              const isVideo = media.type === 'video';
              const isThumbnailFavorited = isFavorite(galleryProduct.id);
              const cellPosterSrc = isVideo
                ? media.poster || galleryProduct.image
                : media.src;
              // Resolve optimized siblings for the cell thumbnail. We gate on
              // the medium (`-960`) variant because the optimize script
              // always emits the three sizes together — if `-960` is missing
              // we treat the whole sibling set as unavailable and silently
              // degrade to just the original `<img>` (e.g. for SVGs or for
              // brand-new images whose siblings haven't been generated yet).
              const cellWebp480 = webpVariant(cellPosterSrc, 480);
              const cellWebp960 = webpVariant(cellPosterSrc, 960);
              const cellWebpFull = webpVariant(cellPosterSrc);
              const cellWebpSrcSet = cellWebp960
                ? `${cellWebp480 ?? cellWebp960} 480w, ${cellWebp960} 960w${
                    cellWebpFull ? `, ${cellWebpFull} 1600w` : ''
                  }`
                : '';
              return (
                <div
                  key={`${media.src}-${index}`}
                  role="button"
                  tabIndex={0}
                  onMouseEnter={() => setActiveImageIndex(index)}
                  onFocus={() => setActiveImageIndex(index)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    e.currentTarget.click();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImageIndex(index);
                    if (typeof onImageClick === 'function') {
                      let previewGallery;
                      if (Array.isArray(media.gallery) && media.gallery.length > 0) {
                        previewGallery = media.gallery.map((item) => {
                          const src = typeof item === 'string' ? item : item.src;
                          const mediaType = item.type
                            || (isVideoSrc(src) ? 'video' : 'image');
                          return {
                            image: src,
                            mediaType,
                            poster: item.poster,
                          };
                        });
                      } else {
                        const relatedMedia = media.groupKey
                          ? galleryMedia.filter((item) => item.groupKey === media.groupKey)
                          : galleryMedia;
                        previewGallery = getUniqueMediaSources(relatedMedia).map((item) => ({
                          image: item.src,
                          mediaType: item.type,
                          poster: item.poster,
                        }));
                      }
                      const imageOnlyPreviewGallery = getProductImageGallery(galleryProduct);
                      const selectedPreviewGallery =
                        previewStartsFromFirstImage && imageOnlyPreviewGallery.length > 0
                          ? imageOnlyPreviewGallery
                          : previewGallery;
                      const primaryPreviewItem =
                        selectedPreviewGallery[0] || {
                          image: media.src,
                          mediaType: media.type,
                          poster: media.poster,
                        };
                      const activePreviewIndex = previewStartsFromFirstImage
                        ? 0
                        : selectedPreviewGallery.findIndex(
                            (item) =>
                              item.image === media.src &&
                              item.mediaType === media.type
                          );
                      onImageClick({
                        image: previewStartsFromFirstImage
                          ? primaryPreviewItem.image
                          : media.src,
                        mediaType: previewStartsFromFirstImage
                          ? primaryPreviewItem.mediaType
                          : media.type,
                        poster: previewStartsFromFirstImage
                          ? primaryPreviewItem.poster
                          : media.poster,
                        activePreviewIndex:
                          activePreviewIndex >= 0 ? activePreviewIndex : 0,
                        caption,
                        product: galleryProduct,
                        galleryImages: selectedPreviewGallery,
                      });
                    }
                  }}
                  aria-label={`${isVideo ? 'Play video' : 'View'} ${caption.title} of ${galleryProduct.name}`}
                  className="group/cell flex flex-col items-stretch text-left cursor-pointer"
                >
                  <div
                    className={`relative aspect-square w-full overflow-hidden rounded-md border-2 transition-all duration-200 ${
                      isActive
                        ? 'border-amber-400 ring-2 ring-amber-400/30'
                        : 'border-gray-200 group-hover/cell:border-amber-300'
                    }`}
                  >
                    <picture className="absolute inset-0 block h-full w-full">
                      {cellWebp960 && (
                        <source
                          type="image/webp"
                          srcSet={cellWebpSrcSet}
                          sizes="(min-width: 1024px) 160px, (min-width: 640px) 25vw, 50vw"
                        />
                      )}
                      <img
                        src={cellPosterSrc}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="block h-full w-full object-cover"
                      />
                    </picture>
                    <button
                      type="button"
                      onClick={(e) => handleThumbnailFavoriteClick(e, galleryProduct)}
                      aria-label={
                        isThumbnailFavorited
                          ? `Remove ${galleryProduct.name} from wishlist`
                          : `Save ${galleryProduct.name} to wishlist`
                      }
                      title={
                        isThumbnailFavorited ? 'Remove from wishlist' : 'Save to wishlist'
                      }
                      className={`absolute top-1 right-1 z-10 flex h-4 w-4 items-center justify-center rounded-full border shadow-sm transition-all duration-200 ${
                        isThumbnailFavorited
                          ? 'border-red-200 bg-white text-red-500'
                          : 'border-white/70 bg-white/90 text-gray-500 hover:text-red-500'
                      }`}
                    >
                      <svg
                        className="h-2.5 w-2.5"
                        fill={isThumbnailFavorited ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                    </button>
                    {!isVideo && (
                      <button
                        type="button"
                        onClick={(e) => handleThumbnailAddToCart(e, galleryProduct)}
                        aria-label={`Add ${galleryProduct.name} to cart`}
                        className="absolute bottom-1.5 right-1.5 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 p-0 text-gray-950 opacity-100 shadow-md transition-all duration-200 hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-white sm:bottom-2 sm:left-1/2 sm:right-auto sm:h-auto sm:w-auto sm:-translate-x-1/2 sm:translate-y-2 sm:gap-1 sm:whitespace-nowrap sm:rounded-md sm:px-2.5 sm:py-1.5 sm:text-[9px] sm:font-bold sm:uppercase sm:tracking-wide sm:opacity-0 sm:shadow-lg sm:group-hover/cell:translate-y-0 sm:group-hover/cell:opacity-100 sm:group-focus-within/cell:translate-y-0 sm:group-focus-within/cell:opacity-100"
                      >
                        <svg
                          className="h-2.5 w-2.5 sm:h-3 sm:w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2.25}
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13 5.4 5M7 13l-2 4h13M9 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm8 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                          />
                        </svg>
                        <span className="hidden sm:inline">Add to Cart</span>
                      </button>
                    )}
                    {isVideo && (
                      <span
                        className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover/cell:bg-black/15 transition-colors"
                        aria-hidden="true"
                      >
                        <span className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/90 shadow-md ring-1 ring-black/10">
                          <svg
                            className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-900 ml-0.5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                      </span>
                    )}
                    {isVideo && (
                      <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                        Video
                      </span>
                    )}
                  </div>
                  {showCaptions && (
                    <>
                      <p
                        className={`mt-1.5 text-[10px] sm:text-[11px] leading-tight font-semibold line-clamp-1 text-center transition-colors duration-200 ${
                          isActive ? 'text-amber-600' : 'text-gray-800 group-hover/cell:text-gray-900'
                        }`}
                      >
                        {caption.title}
                      </p>
                      {(priceProduct || caption.price) && (
                        <div className="mt-0.5 flex justify-center">
                          {priceProduct ? (
                            <ProductPrice
                              product={priceProduct}
                              size="xs"
                              stacked
                              className={`justify-center transition-colors duration-200 ${
                                isActive ? 'text-gray-950' : 'text-gray-800'
                              }`}
                            />
                          ) : (
                            <p
                              className={`text-[9px] sm:text-[10px] leading-tight font-bold text-center transition-colors duration-200 ${
                                isActive ? 'text-gray-950' : 'text-gray-800'
                              }`}
                            >
                              {caption.price}
                            </p>
                          )}
                        </div>
                      )}
                      <p
                        className={`mt-0.5 text-[8px] sm:text-[9px] leading-snug font-normal line-clamp-2 text-center transition-colors duration-200 ${
                          isActive ? 'text-amber-700/80' : 'text-gray-500 group-hover/cell:text-gray-700'
                        }`}
                      >
                        {caption.body}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-4 py-3" aria-hidden="true" />
    </div>
  );
};

export default ProductCard;
